/**
 * SceneRefsPanel — per-scene Look & Feel reference uploads.
 *
 * Scoped to a single scene (`scene` prop = scene_number, e.g. "SC01"). Uploads
 * flow into project.look_feel_scene_refs[scene] on the backend and drive that
 * scene's keyframe-prompt look injection. Below the thumbnails we show a
 * CONCISE, NON-EDITABLE summary of the decoded look (no one edits the 13-field
 * object — it's a confirmation, not a form).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, X, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { pollJobUntilDone } from '../hooks/useAsyncJob';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_REFS = 20;

// The few fields worth surfacing as a human-readable summary. The rest of the
// 13-field decode still drives generation server-side — it's just not shown.
const SUMMARY_FIELDS = [
  ['lighting_philosophy', 'Lighting'],
  ['palette', 'Palette'],
  ['contrast', 'Contrast'],
  ['reference_feel', 'Reference feel'],
];

export function SceneRefsPanel({ projectId, scene }) {
  const [refs, setRefs] = useState([]);
  const [decoded, setDecoded] = useState({});
  const [uploading, setUploading] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const decodeTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!projectId || !scene) return;
    try {
      const [refsRes, decodedRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/look-feel-refs`, { params: { scene } }),
        axios.get(`${API}/projects/${projectId}/look-feel-decoded`, { params: { scene } }),
      ]);
      setRefs(refsRes.data?.items || []);
      setDecoded(decodedRes.data?.decoded || {});
    } catch (e) {
      /* new scene may 404 — ignore */
    }
  }, [projectId, scene]);

  useEffect(() => { load(); }, [load]);

  const runDecode = useCallback(async (force = false) => {
    if (decoding) return;
    setDecoding(true);
    try {
      // Decode is an ASYNC JOB now ({job_id}); drive it via the async-job infra.
      const { data: kick } = await axios.post(`${API}/projects/${projectId}/look-feel-decode`, { force, scene });
      if (kick?.job_id) {
        const job = await pollJobUntilDone(API, kick.job_id);
        if (job.status === 'ready') {
          setDecoded(job.result?.decoded || {});
          await load();
          toast.success(`${scene} look decoded — applied to this scene's shots (re-grade)`);
        } else {
          toast.error(`Decode failed: ${job.error || 'the worker stopped responding'}`);
        }
      } else {
        setDecoded(kick?.decoded || {});
        await load();
        toast.success(`${scene} look decoded — injected into this scene's keyframe prompts`);
      }
    } catch (e) {
      toast.error(`Decode failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDecoding(false);
    }
  }, [decoding, projectId, scene, load]);

  const applyToScene = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/scenes/${scene}/reenforce-llg`);
      const updated = (data?.updated || []).length;
      const locked = data?.skipped_locked || [];
      const unchanged = (data?.unchanged || []).length;
      if (updated > 0) {
        toast.success(
          `${scene} look applied to ${updated} shot${updated === 1 ? '' : 's'}` +
          (locked.length ? ` · skipped ${locked.length} locked (${locked.join(', ')})` : '')
        );
      } else if (locked.length && (updated + unchanged) === 0) {
        toast.info(`${scene}: all ${locked.length} shot${locked.length === 1 ? '' : 's'} locked — nothing to apply (${locked.join(', ')})`);
      } else {
        toast.success(
          `${scene} already up to date — ${unchanged} shot${unchanged === 1 ? '' : 's'} current, nothing changed` +
          (locked.length ? ` · ${locked.length} locked` : '')
        );
      }
    } catch (e) {
      toast.error(`Apply failed: ${e.response?.data?.detail || e.message}`);
    }
  }, [projectId, scene]);

  const scheduleAutoDecode = useCallback(() => {
    if (decodeTimerRef.current) clearTimeout(decodeTimerRef.current);
    decodeTimerRef.current = setTimeout(() => runDecode(true).catch(() => {}), 3000);
  }, [runDecode]);

  const handleUpload = useCallback(async (files) => {
    if (!files?.length || !projectId) return;
    if (refs.length + files.length > MAX_REFS) {
      toast.error(`Can only hold ${MAX_REFS} refs per scene. Currently ${refs.length}.`);
      return;
    }
    setUploading(true);
    try {
      for (const file of Array.from(files).slice(0, MAX_REFS - refs.length)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('use_for', 'lighting,color,mood,texture');
        fd.append('scene', scene);
        await axios.post(`${API}/projects/${projectId}/look-feel-refs`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await load();
      scheduleAutoDecode();
      toast.success(`Uploaded ${files.length} ref${files.length > 1 ? 's' : ''} to ${scene} — decoding in 3s…`);
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
    }
  }, [refs.length, projectId, scene, load, scheduleAutoDecode]);

  const handleDelete = useCallback(async (refId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/look-feel-refs/${refId}`, { params: { scene } });
      await load();
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  }, [projectId, scene, load]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleUpload(e.dataTransfer?.files);
  }, [handleUpload]);

  const summaryItems = SUMMARY_FIELDS
    .map(([k, label]) => [label, (decoded[k] || '').toString().trim()])
    .filter(([, v]) => v);
  const hasDecoded = summaryItems.length > 0;

  return (
    <div className="mt-2 rounded-xl border border-fuchsia-500/20 bg-fuchsia-950/10 p-3" data-testid={`scene-refs-panel-${scene}`}>
      <div className="text-[10.5px] text-white/40 mb-2" data-testid={`scene-refs-hint-${scene}`}>
        Best results: one reference per scene — multiple refs average together.
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={handleDrop}
        className="border-2 border-dashed border-fuchsia-500/25 rounded-lg p-3 min-h-[84px] hover:border-fuchsia-500/45 transition-colors"
      >
        <div className="flex flex-wrap gap-2 items-start">
          {refs.map((r) => (
            <div key={r.id} className="relative group" data-testid={`scene-ref-${scene}-${r.id}`}>
              <img src={r.image_url} alt="ref" className="w-14 h-14 object-cover rounded-md border border-white/10" />
              <button
                onClick={() => handleDelete(r.id)}
                className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`scene-ref-delete-${scene}-${r.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {refs.length < MAX_REFS && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-14 h-14 rounded-md border border-dashed border-fuchsia-400/40 flex flex-col items-center justify-center gap-0.5 text-fuchsia-300/70 hover:text-fuchsia-200 hover:border-fuchsia-400/70 disabled:opacity-40"
              data-testid={`scene-upload-btn-${scene}`}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" /><span className="text-[9px]">Add</span></>}
            </button>
          )}
          {refs.length === 0 && !uploading && (
            <div className="flex-1 flex items-center justify-center text-[11px] text-white/40 min-h-[56px]">
              <ImageIcon className="w-4 h-4 mr-2" /> Drop scene references or click Add
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/heic"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
          data-testid={`scene-file-input-${scene}`}
        />
      </div>

      {refs.length > 0 && (
        <div className="flex items-center gap-2 mt-2">
          {decoding ? (
            <span className="text-[11px] text-fuchsia-300/80 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Decoding this scene's look…
            </span>
          ) : hasDecoded ? (
            <span className="text-[11px] text-emerald-300/80">✓ Look injected into {scene} keyframes</span>
          ) : (
            <span className="text-[11px] text-white/50">Auto-decode fires 3s after upload</span>
          )}
          <button
            onClick={() => runDecode(true)}
            disabled={decoding}
            className="ml-auto px-2.5 py-1 rounded-md text-[11px] font-medium bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-500/30 hover:bg-fuchsia-500/30 flex items-center gap-1 disabled:opacity-40"
            data-testid={`scene-decode-btn-${scene}`}
          >
            <RefreshCw className={`w-3 h-3 ${decoding ? 'animate-spin' : ''}`} /> Re-decode
          </button>
          {hasDecoded && (
            <button
              onClick={applyToScene}
              disabled={decoding}
              title="Re-apply this scene's decoded look to every unlocked shot in the scene (cheap re-grade — no re-shoot, locked shots skipped)."
              className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-white/5 text-white/70 border border-white/15 hover:bg-white/10 disabled:opacity-40"
              data-testid={`scene-apply-btn-${scene}`}
            >
              Apply to scene
            </button>
          )}
        </div>
      )}

      {hasDecoded && (
        <div className="mt-2 pt-2 border-t border-fuchsia-500/15 space-y-1" data-testid={`scene-decode-summary-${scene}`}>
          {summaryItems.map(([label, value]) => (
            <div key={label} className="flex gap-2 text-[11px] leading-snug">
              <span className="text-fuchsia-300/60 shrink-0 w-24">{label}</span>
              <span className="text-white/70">{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
